// White Label Payment Service - Handles one-time purchases and revenue tracking
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

class WhiteLabelPaymentService {
  /**
   * Check if a tenant has purchased White Label license
   * @param {string} tenantId - The tenant ID to check
   * @returns {Promise<Object>} Purchase status and details
   */
  async checkWhiteLabelPurchase(tenantId) {
    try {
      const purchaseRef = doc(db, 'whitelabel_purchases', tenantId);
      const purchaseSnap = await getDoc(purchaseRef);

      if (purchaseSnap.exists()) {
        const data = purchaseSnap.data();
        return {
          hasPurchase: true,
          purchaseType: data.purchaseType, // 'full_license' or 'revenue_share'
          purchaseDate: data.purchaseDate,
          amountPaid: data.amountPaid,
          status: data.status,
          revenueSharePercentage: data.revenueSharePercentage || 0,
          customerCount: data.customerCount || 0,
          totalRevenueGenerated: data.totalRevenueGenerated || 0,
          royaltiesOwed: data.royaltiesOwed || 0
        };
      }

      return {
        hasPurchase: false
      };
    } catch (error) {
      console.error('Error checking White Label purchase:', error);
      return {
        hasPurchase: false,
        error: error.message
      };
    }
  }

  /**
   * Create Stripe Checkout session for White Label purchase
   * @param {string} tenantId - Tenant making the purchase
   * @param {string} userId - User ID
   * @param {string} userEmail - User's email
   * @param {string} purchaseType - 'full_license' or 'revenue_share'
   * @returns {Promise<Object>} Checkout session URL
   */
  async createCheckoutSession(tenantId, userId, userEmail, purchaseType) {
    try {
      // Payment links (to be configured in Stripe Dashboard)
      const paymentLinks = {
        full_license: process.env.REACT_APP_STRIPE_FULL_LICENSE_LINK || 'https://buy.stripe.com/PLACEHOLDER_FULL',
        revenue_share: process.env.REACT_APP_STRIPE_REVENUE_SHARE_LINK || 'https://buy.stripe.com/PLACEHOLDER_REVENUE'
      };

      if (!paymentLinks[purchaseType]) {
        throw new Error('Invalid purchase type');
      }

      const paymentUrl = paymentLinks[purchaseType];

      // Store payment context for post-payment processing
      const paymentContext = {
        tenantId,
        userId,
        userEmail,
        purchaseType,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem('whitelabel_payment_context', JSON.stringify(paymentContext));

      return {
        success: true,
        sessionUrl: paymentUrl
      };
    } catch (error) {
      console.error('Error creating checkout session:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Record successful White Label purchase
   * @param {string} tenantId - Tenant ID
   * @param {Object} purchaseData - Purchase details
   * @returns {Promise<Object>} Success status
   */
  async recordPurchase(tenantId, purchaseData) {
    try {
      const purchaseRef = doc(db, 'whitelabel_purchases', tenantId);

      const data = {
        tenantId,
        purchaseType: purchaseData.purchaseType,
        purchaseDate: new Date().toISOString(),
        amountPaid: purchaseData.amountPaid,
        stripePaymentId: purchaseData.stripePaymentId || null,
        stripeSessionId: purchaseData.stripeSessionId || null,
        status: 'active',
        revenueSharePercentage: purchaseData.purchaseType === 'revenue_share' ? 10 : 0,
        customerCount: 0,
        totalRevenueGenerated: 0,
        royaltiesOwed: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await setDoc(purchaseRef, data);

      // Also update tenant record to reflect White Label access
      const tenantRef = doc(db, 'MarketGenie_tenants', tenantId);
      await updateDoc(tenantRef, {
        hasWhiteLabel: true,
        whiteLabelType: purchaseData.purchaseType,
        whiteLabelActivatedAt: new Date().toISOString()
      });

      return {
        success: true,
        message: 'White Label purchase recorded successfully'
      };
    } catch (error) {
      console.error('Error recording purchase:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Track customer revenue for revenue share partners
   * @param {string} partnerTenantId - Partner's tenant ID
   * @param {number} amount - Revenue amount
   * @returns {Promise<Object>} Updated revenue data
   */
  async trackPartnerRevenue(partnerTenantId, amount) {
    try {
      const purchaseRef = doc(db, 'whitelabel_purchases', partnerTenantId);
      const purchaseSnap = await getDoc(purchaseRef);

      if (!purchaseSnap.exists()) {
        throw new Error('Partner purchase record not found');
      }

      const purchase = purchaseSnap.data();
      
      // Only track if revenue share type
      if (purchase.purchaseType !== 'revenue_share') {
        return {
          success: true,
          message: 'Full license - no revenue share tracking needed'
        };
      }

      const revenueSharePercentage = purchase.revenueSharePercentage || 10;
      const royalty = amount * (revenueSharePercentage / 100);

      await updateDoc(purchaseRef, {
        totalRevenueGenerated: (purchase.totalRevenueGenerated || 0) + amount,
        royaltiesOwed: (purchase.royaltiesOwed || 0) + royalty,
        updatedAt: new Date().toISOString()
      });

      return {
        success: true,
        revenueAdded: amount,
        royaltyAdded: royalty,
        totalRevenue: (purchase.totalRevenueGenerated || 0) + amount,
        totalRoyalties: (purchase.royaltiesOwed || 0) + royalty
      };
    } catch (error) {
      console.error('Error tracking partner revenue:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Increment customer count for partner
   * @param {string} partnerTenantId - Partner's tenant ID
   * @returns {Promise<Object>} Updated customer count
   */
  async incrementCustomerCount(partnerTenantId) {
    try {
      const purchaseRef = doc(db, 'whitelabel_purchases', partnerTenantId);
      const purchaseSnap = await getDoc(purchaseRef);

      if (!purchaseSnap.exists()) {
        throw new Error('Partner purchase record not found');
      }

      const purchase = purchaseSnap.data();

      await updateDoc(purchaseRef, {
        customerCount: (purchase.customerCount || 0) + 1,
        updatedAt: new Date().toISOString()
      });

      return {
        success: true,
        customerCount: (purchase.customerCount || 0) + 1
      };
    } catch (error) {
      console.error('Error incrementing customer count:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Get all active White Label partners (for admin dashboard)
   * @returns {Promise<Array>} List of partners
   */
  async getAllPartners() {
    try {
      const purchasesRef = collection(db, 'whitelabel_purchases');
      const q = query(purchasesRef, where('status', '==', 'active'));
      const snapshot = await getDocs(q);

      const partners = [];
      snapshot.forEach(doc => {
        partners.push({
          tenantId: doc.id,
          ...doc.data()
        });
      });

      return {
        success: true,
        partners
      };
    } catch (error) {
      console.error('Error getting partners:', error);
      return {
        success: false,
        error: error.message,
        partners: []
      };
    }
  }

  /**
   * Calculate total royalties owed across all revenue share partners
   * @returns {Promise<Object>} Total royalties
   */
  async getTotalRoyaltiesOwed() {
    try {
      const purchasesRef = collection(db, 'whitelabel_purchases');
      const q = query(purchasesRef, where('purchaseType', '==', 'revenue_share'));
      const snapshot = await getDocs(q);

      let totalRoyalties = 0;
      let partnerCount = 0;

      snapshot.forEach(doc => {
        const data = doc.data();
        totalRoyalties += data.royaltiesOwed || 0;
        partnerCount++;
      });

      return {
        success: true,
        totalRoyalties,
        partnerCount
      };
    } catch (error) {
      console.error('Error calculating royalties:', error);
      return {
        success: false,
        error: error.message,
        totalRoyalties: 0,
        partnerCount: 0
      };
    }
  }

  /**
   * Mark royalties as paid for a partner
   * @param {string} partnerTenantId - Partner's tenant ID
   * @param {number} amountPaid - Amount paid
   * @returns {Promise<Object>} Success status
   */
  async markRoyaltiesPaid(partnerTenantId, amountPaid) {
    try {
      const purchaseRef = doc(db, 'whitelabel_purchases', partnerTenantId);

      await updateDoc(purchaseRef, {
        royaltiesOwed: 0,
        lastPaymentDate: new Date().toISOString(),
        lastPaymentAmount: amountPaid,
        updatedAt: new Date().toISOString()
      });

      return {
        success: true,
        message: 'Royalties marked as paid'
      };
    } catch (error) {
      console.error('Error marking royalties paid:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

export default new WhiteLabelPaymentService();
